import { useXFetch } from '~/shared/utils/useXFetch';
import { Account } from '~/shared/utils/types';
import { isInstanceFamilyLevel } from './utils';

export type ConfigurationLevel =
  | 'org'
  | 'account'
  | 'cluster'
  | 'instance'
  | 'node'
  | 'volume';
export type ConfigurationValueOrigin = 'default' | 'inherited' | 'overridden';

interface ConfigurationQueryKeyParams {
  nodeId: string | null;
  level?: ConfigurationLevel;
  clusterId?: string;
  instanceId?: string;
  region?: string;
  accountId?: string;
}

export const getStableAccountsKey = (accounts: Account[] | undefined): string =>
  (accounts ?? [])
    .map((a) => a.accountId)
    .sort()
    .join(',');

export const configurationTreeQueryKey = (
  accounts: Account[] | undefined,
  mode?: string,
  accountsScope?: string
) => ['configurationTree', mode ?? 'full', getStableAccountsKey(accounts), accountsScope ?? 'self'];

export const configurationQueryKey = ({
  nodeId,
  level,
  clusterId,
  instanceId,
  region,
  accountId,
}: ConfigurationQueryKeyParams) =>
  [
    'configuration',
    nodeId ?? null,
    level ?? null,
    clusterId ?? null,
    instanceId ?? null,
    region ?? null,
    accountId ?? null,
  ].filter((key) => key !== null);

export interface ConfigurationTreeResponse {
  id: string;
  level: ConfigurationLevel;
  hasOverrides: boolean;
  children?: ConfigurationTreeResponse[];
}

export interface ConfigurationTreeApiResponse {
  tree: ConfigurationTreeResponse;
}

export interface ConfigurationResponse {
  updated?: string;
  configurations: Record<string, string> | null;
  configurationsWithOverrides: Record<
    string,
    {
      value: string;
      valueOrigin: ConfigurationValueOrigin;
      inheritedFrom?: {
        layer: string;
        entityId: string;
      };
    }
  > | null;
  level: ConfigurationLevel;
  clusterId?: string;
  instanceId?: string;
}

export interface RevertConfigurationRequest {
  level: ConfigurationLevel;
  keys: string | string[];
  accountId: string;
  clusterId?: string;
  instanceId?: string;
}

export interface RevertConfigurationResponse {
  configurations: Record<string, string>;
  level: ConfigurationLevel;
  clusterId?: string;
  instanceId?: string;
}

export interface UpdateConfigurationRequest {
  configurations: Record<string, string>;
  level: ConfigurationLevel;
  clusterId?: string;
  instanceId?: string;
  accountId?: string;
  region: string;
}

export interface UpdateConfigurationResponse {
  updated: string;
  configurations: Record<string, string> | null;
  configurationsWithOverrides: Record<
    string,
    {
      value: string;
      valueOrigin: ConfigurationValueOrigin;
      inheritedFrom?: {
        layer: string;
        entityId: string;
      };
    }
  > | null;
  level: ConfigurationLevel;
  clusterId?: string;
  instanceId?: string;
}

export async function getConfigurationTree(
  api: ReturnType<typeof useXFetch>,
  mode: 'full' | 'edited',
  options?: {
    instanceId?: string;
    limit?: number;
    offset?: number;
    accountsScope?: string;
  }
): Promise<ConfigurationTreeResponse> {
  const params = new URLSearchParams({ mode });
  if (options?.accountsScope) {
    params.append('accounts', options.accountsScope);
  }
  if (options?.instanceId) {
    params.append('instanceId', options.instanceId);
  }
  if (options?.limit !== undefined) {
    params.append('limit', String(options.limit));
  }
  if (options?.offset !== undefined) {
    params.append('offset', String(options.offset));
  }
  const url = `/api/v1/configuration/tree?${params.toString()}`;
  const response = await api<ConfigurationTreeApiResponse>(url);
  return response.tree;
}

export async function getConfiguration(
  api: ReturnType<typeof useXFetch>,
  params: {
    level: ConfigurationLevel;
    clusterId?: string;
    instanceId?: string;
    region?: string;
    accountId?: string;
    includeOverrides: boolean;
  }
): Promise<ConfigurationResponse> {
  const queryParams = new URLSearchParams({
    level: params.level,
    includeOverrides: String(params.includeOverrides),
  });

  if (params.clusterId) {
    queryParams.append('clusterId', params.clusterId);
  }
  if (params.instanceId) {
    queryParams.append('instanceId', params.instanceId);
  }
  if (params.region) {
    queryParams.append('region', params.region);
  }
  if (params.accountId) {
    queryParams.append('accountId', params.accountId);
  }

  const url = `/api/v1/configuration?${queryParams.toString()}`;
  return api<ConfigurationResponse>(url);
}

export interface DefaultFieldMetadata {
  name: string;
  description: string;
  type: 'boolean' | 'positive_float' | 'duration' | 'size' | 'percentage' | 'enum' | 'string';
  customerFacing: boolean;
  value: string;
  validValues?: string[];
  minValue?: string;
  maxValue?: string;
}

export interface ConfigurationDefaultsResponse {
  configurations: Record<string, DefaultFieldMetadata>;
  retrieved: string;
}

export async function getConfigurationDefaults(
  api: ReturnType<typeof useXFetch>,
  params: {
    level: ConfigurationLevel;
    clusterId?: string;
    instanceId?: string;
    region?: string;
    accountId?: string;
  }
): Promise<Record<string, DefaultFieldMetadata>> {
  const queryParams = new URLSearchParams({
    level: params.level,
  });

  if (params.clusterId) {
    queryParams.append('id', params.clusterId);
  }
  if (params.instanceId) {
    queryParams.append('id', params.instanceId);
  }
  if (params.region) {
    queryParams.append('region', params.region);
  }
  if (params.accountId) {
    queryParams.append('accountId', params.accountId);
  }

  const url = `/api/v1/configuration/defaults?${queryParams.toString()}`;
  const response = await api<ConfigurationDefaultsResponse>(url);

  return response.configurations;
}

export async function revertConfiguration(
  api: ReturnType<typeof useXFetch>,
  body: RevertConfigurationRequest
): Promise<RevertConfigurationResponse> {
  const url = '/api/v1/configuration/revert';

  const isRevertAll =
    (Array.isArray(body.keys) && body.keys.length === 1 && body.keys[0] === '*') ||
    body.keys === '*';

  let requestBody: string;

  if (isRevertAll) {
    // Manually construct JSON with [*] instead of ["*"]
    const jsonParts: string[] = [
      `"level":${JSON.stringify(body.level)}`,
      `"keys":["*"]`,
      `"accountId":${JSON.stringify(body.accountId)}`,
    ];
    if (body.clusterId) {
      jsonParts.push(`"clusterId":${JSON.stringify(body.clusterId)}`);
    }
    if (body.instanceId) {
      jsonParts.push(`"instanceId":${JSON.stringify(body.instanceId)}`);
    }
    requestBody = `{${jsonParts.join(',')}}`;
  } else {
    // Normal JSON stringify for other cases
    const cleanBody: Record<string, unknown> = {
      level: body.level,
      keys: body.keys,
      accountId: body.accountId,
    };
    if (body.clusterId) {
      cleanBody.clusterId = body.clusterId;
    }
    if (body.instanceId) {
      cleanBody.instanceId = body.instanceId;
    }
    requestBody = JSON.stringify(cleanBody);
  }

  return api<RevertConfigurationResponse>(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: requestBody,
  });
}

export async function updateConfiguration(
  api: ReturnType<typeof useXFetch>,
  body: UpdateConfigurationRequest
): Promise<UpdateConfigurationResponse> {
  const url = '/api/v1/configuration';
  const cleanBody: Record<string, unknown> = {
    configurations: body.configurations,
    level: body.level,
    region: body.region,
  };
  if (body.level === 'cluster' && body.clusterId) {
    cleanBody.clusterId = body.clusterId;
  }
  if (isInstanceFamilyLevel(body.level) && body.instanceId) {
    cleanBody.instanceId = body.instanceId;
  }
  if ((body.level === 'account' || isInstanceFamilyLevel(body.level)) && body.accountId) {
    cleanBody.accountId = body.accountId;
  }

  // If accessToken is provided, use fetch directly to read error response body
  // if (accessToken) {
  //   const baseUrl = runtimeEnv.apiBaseUrl.replace(/\/$/, '');
  //   const uri = `${baseUrl}/${url.replace(/^\//, '')}`;

  //   const response = await fetch(uri, {
  //     method: 'PUT',
  //     mode: 'cors',
  //     headers: {
  //       'Content-Type': 'application/json',
  //       Authorization: `Bearer ${accessToken}`,
  //     },
  //     body: JSON.stringify(cleanBody),
  //   });

  //   if (!response.ok) {
  //     // Read the error response body to extract the actual error message
  //     let errorMessage = response.statusText;
  //     try {
  //       const errorBody = await response.json();
  //       if (errorBody?.message) {
  //         errorMessage = errorBody.message;
  //       }
  //     } catch {
  //       // If JSON parsing fails, use statusText
  //       errorMessage = response.statusText;
  //     }

  //     const error = new Error(errorMessage) as Error & { status: number };
  //     error.status = response.status;
  //     throw error;
  //   }

  //   const data = await response.json();
  //   return data;
  // }

  // Fallback to using api function if no accessToken provided
  const response = api<UpdateConfigurationResponse>(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(cleanBody),
  });
  return response;
}

export interface AddConfigurationTypeRequest {
  confName: string;
  isShellConf: boolean;
  isCoreConf: boolean;
  isSaasConf: boolean;
  paramType:
  | 'boolean'
  | 'duration'
  | 'size'
  | 'percentage'
  | 'positive_float'
  | 'positive_integer'
  | 'enum'
  | 'string';
  customerFacing: boolean;
  defaultValue: string;
  validValues?: string[];
  description?: string;
}

export interface AddConfigurationTypeResponse {
  confName: string;
  paramType: string;
  defaultValue: string;
  added: boolean;
}

export async function addConfigurationType(
  api: ReturnType<typeof useXFetch>,
  body: AddConfigurationTypeRequest
): Promise<AddConfigurationTypeResponse> {
  const url = '/api/v1/configuration/types';
  return api<AddConfigurationTypeResponse>(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
}
